import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { BookOpen, ArrowRight } from "lucide-react";
import api from "@/lib/api";
import Seo, { breadcrumbSchema, orgSchema } from "@/components/Seo";

const SITE = "https://signiastylettoix.com";

export default function LearnArticle() {
    const { slug } = useParams();
    const [a, setA] = useState(null);
    const [error, setError] = useState(false);

    useEffect(() => {
        let cancelled = false;
        setA(null);
        api.get(`/learn/articles/${slug}`)
            .then((r) => { if (!cancelled) setA(r.data); })
            .catch(() => { if (!cancelled) setError(true); });
        window.scrollTo(0, 0);
        return () => { cancelled = true; };
    }, [slug]);

    if (error) return <div className="min-h-[60vh] flex items-center justify-center text-[#4a5568]">Article not found. <Link className="underline ml-2" to="/learn">Browse the Knowledge Centre</Link></div>;
    if (!a) return <div className="min-h-[60vh] flex items-center justify-center text-[#4a5568]">Loading…</div>;

    const url = `${SITE}/learn/${a.slug}`;
    const schema = {
        "@context": "https://schema.org",
        "@graph": [
            orgSchema(),
            breadcrumbSchema([{ name: "Home", url: SITE + "/" }, { name: "Knowledge Centre", url: SITE + "/learn" }, { name: a.title, url }]),
            { "@type": "Article", headline: a.title, description: a.meta_description, mainEntityOfPage: url, author: { "@type": "Organization", name: "SigniaStylettoIX — HearClear Clinical Team" }, publisher: { "@type": "Organization", name: "SigniaStylettoIX.com" } },
        ],
    };

    return (
        <>
            <Seo title={a.title} description={a.meta_description} canonical={url} schema={schema} />
            <section className="pt-12 md:pt-16 pb-16">
                <div className="container-x">
                    <nav className="text-[12.5px] text-[#4a5568] mb-6">
                        <Link to="/" className="hover:text-[#003E4D]">Home</Link><span className="mx-2 text-[#9CA3AF]">/</span>
                        <Link to="/learn" className="hover:text-[#003E4D]">Knowledge Centre</Link><span className="mx-2 text-[#9CA3AF]">/</span>
                        <span className="text-[#1a1a1a]">{a.title}</span>
                    </nav>
                    <p className="eyebrow inline-flex items-center gap-2"><BookOpen size={12} /> {a.category.replace("-", " ")} · {a.read_minutes} min read</p>
                    <h1 className="font-serif text-[34px] md:text-[46px] leading-[1.1] mt-4 max-w-[840px]" data-testid="article-h1">{a.title}</h1>
                    <div className="mt-10 max-w-[780px] space-y-10">
                        {a.sections.map((s, i) => (
                            <div key={i}>
                                <h2 className="font-serif text-[24px] md:text-[28px] text-[#003E4D] mb-3">{s.h2}</h2>
                                <div className="text-[16px] text-[#333] leading-relaxed space-y-3 [&_a]:text-[#0F6B7D] [&_a]:underline [&_ol]:list-decimal [&_ol]:pl-6 [&_ul]:list-disc [&_ul]:pl-6" dangerouslySetInnerHTML={{ __html: s.body }} />
                            </div>
                        ))}
                    </div>
                    <div className="mt-14 max-w-[820px]">
                        <h2 className="font-serif text-[24px] text-[#003E4D] mb-4">Related articles</h2>
                        <div className="grid md:grid-cols-2 gap-4" data-testid="related-articles">
                            {(a.related || []).map((r) => (
                                <Link key={r.slug} to={`/learn/${r.slug}`} className="block bg-white border border-[#e2ddd4] rounded-xl p-5 hover:border-[#003E4D]">
                                    <span className="text-[15px] font-medium text-[#1a1a1a]">{r.title}</span>
                                </Link>
                            ))}
                        </div>
                        <Link to="/book-trial" className="btn-primary mt-8 inline-flex" data-testid="article-trial-cta">Book a Free Hearing Test <ArrowRight size={16} /></Link>
                    </div>
                </div>
            </section>
        </>
    );
}
